import type { AxiosResponse } from "axios";
import api from "../api";
import type {
  VariableDeleteResponse,
  VariableRecoverResponse,
  VariableResponse,
  VariablesPageResponse,
} from "../../../../shared/apiTypes";
import {
  ADD_VARIABLE,
  DELETE_VARIABLE,
  GET_VARIABLES,
  UPDATE_VARIABLE,
  ADD_ERROR,
  GET_ERROR,
  DELETE_ERROR,
  UPDATE_ERROR,
} from "./types";
import { GET_ERRORS } from "../errorServices/errorTypes";
import { asCaught, errorPayload as caughtErrorPayload } from "../caughtError";
export const addVariable = (variableData) => async (dispatch) => {
  try {
    const res: AxiosResponse<VariableResponse> = await api.post(
      "/api/variables/add",
      variableData
    );
    dispatch({
      type: ADD_VARIABLE,
      payload: res.data,
    });
    return res.data;
  } catch (err) {
    dispatch({
      type: ADD_ERROR,
      payload: caughtErrorPayload(err),
    });
    throw err;
  }
};

const fetchVariablesPage = async (dispatch, limit, skip) => {
  const res: AxiosResponse<VariablesPageResponse> = await api.get(
    `/api/variables/list_pagination?limit=${limit}&skip=${skip}`
  );

  const { variables, configuration } = res.data;

  dispatch({
    type: GET_VARIABLES,
    payload: {
      variables,
      configuration,
    },
  });

  return { received: variables.length, size: configuration.size };
};

export const getVariables = (limit = 20, skip = 0) => async (dispatch) => {
  try {
    const { received, size } = await fetchVariablesPage(dispatch, limit, skip);

    // The screen needs the whole catalog (it paginates client-side), so fetch the
    // remainder in ONE follow-up request instead of recursing page-by-page:
    // 2 round-trips instead of ~20 sequential ones (SCRUM-1112). The catalog is
    // small enough to fit a single response (~132 KB raw for 381 variables).
    const loaded = skip + received;

    if (received > 0 && size > loaded) {
      await fetchVariablesPage(dispatch, size - loaded, loaded);
    }
  } catch (err) {
    const errorPayload = asCaught(err).response
      ? asCaught(err).response?.data
      : { message: "An unexpected error occurred." };

    console.error("Error fetching variables:", errorPayload);

    dispatch({
      type: GET_ERROR,
      payload: errorPayload,
    });
    // GET_ERROR only fills `state.variables.errors.get`, which no screen reads, so
    // a failed load showed an empty list and nothing else. Route it to the
    // notification hub as well (SCRUM-2001).
    dispatch({ type: GET_ERRORS, payload: errorPayload });
  }
};

//Delete Variable
export const deleteVariable = (id) => async (dispatch) => {
  try {
    await api.delete<VariableDeleteResponse>(`/api/variables/delete/${id}`);
    dispatch({
      type: DELETE_VARIABLE,
      payload: id,
    });
    return id;
  } catch (err) {
    dispatch({
      type: DELETE_ERROR,
      payload: caughtErrorPayload(err),
    });
    throw err;
  }
};

// SCRUM-1820: the two answers to a 409 from /add whose body names a deleted
// variable (`deletedId`). Restore brings it back into the list; release frees
// its title and code_name so the same add can be sent again. Both re-throw, so
// the form keeps its offer on screen when the call fails.
export const restoreVariable = (id) => async (dispatch) => {
  const res: AxiosResponse<VariableRecoverResponse> = await api.post(`/api/variables/${id}/restore`);
  dispatch({
    type: ADD_VARIABLE,
    payload: res.data,
  });
  return res.data;
};

export const releaseVariable = (id) => async () => {
  const res: AxiosResponse<VariableRecoverResponse> = await api.post(`/api/variables/${id}/release`);
  return res.data;
};

//update variable
export const updateVariable = (data) => async (dispatch) => {
  try {
    const res: AxiosResponse<VariableResponse> = await api.put(
      "/api/variables/update",
      data
    );
    dispatch({
      type: UPDATE_VARIABLE,
      payload: res.data,
    });
    return res.data;
  } catch (err) {
    dispatch({
      type: UPDATE_ERROR,
      payload: caughtErrorPayload(err),
    });
    throw err;
  }
};
